const acknowledgeContent = (state, id) => ({
  ...state,
  [id]: {
    ...state[id],
    acknowledgement: {
      ...state[id].acknowledgement,
      acknowledged_at: new Date(),
    },
  },
});

const unacknowledgeContent = (state, id) => ({
  ...state,
  [id]: {
    ...state[id],
    acknowledgement: {
      ...state[id].acknowledgement,
      acknowledged_at: null,
    },
  },
});

const addContent = (state, content) => {
  const existing = state[content.id];
  if (!existing) return { ...state, [content.id]: content };

  // Some sources (e.g. sparse payloads missing pdf_export_enabled — FE-35715)
  // omit keys a prior, richer record already set. Only overwrite when this
  // entry actually carries the key — otherwise keep whatever's already
  // stored. Assumes server-origin content objects, where an absent key (not
  // requested) and an explicit `undefined` value never occur together.
  const pdfExportEnabled = Object.prototype.hasOwnProperty.call(
    content,
    'pdf_export_enabled'
  )
    ? content.pdf_export_enabled
    : existing.pdf_export_enabled;

  return {
    ...state,
    [content.id]: {
      ...content,
      pdf_export_enabled: pdfExportEnabled,
    },
  };
};

const addContents = (state, contents) => {
  const newContents = contents.reduce((obj, content) => {
    // Check obj (this dispatch) before state (the prior one) — the same
    // content can appear twice in one dispatch when it's returned by more
    // than one homepage module.
    const existing = obj[content.id] || state[content.id];
    if (!existing) return { ...obj, [content.id]: content };

    // Preserve local engagement state only when it diverges from the server —
    // this handles the read-replica lag window where the server still returns
    // the pre-action value right after a like/bookmark action.
    const liked =
      existing.liked !== content.liked ? existing.liked : content.liked;
    const bookmarked =
      existing.bookmarked !== content.bookmarked
        ? existing.bookmarked
        : content.bookmarked;
    const incomingLikes = content.reaction_counts?.likes;
    const existingLikes = existing.reaction_counts?.likes;
    const likes =
      existing.liked !== content.liked
        ? existingLikes ?? incomingLikes
        : incomingLikes;

    // Also preserve per-reaction counts during replica lag so the reaction count
    // display doesn't flash to 0 while the server catches up.
    // Guard on EITHER liked mismatch OR likes-count mismatch — the count can lag
    // independently (e.g. unlike: liked syncs to false immediately but count stays 1).
    const expandedReactions =
      existing.liked !== content.liked || existingLikes !== incomingLikes
        ? existing.expanded_reactions ?? content.expanded_reactions
        : content.expanded_reactions;

    // Some homepage modules opt into body_excerpt (include_body_excerpt) and
    // some don't, so the same content can arrive with and without it. Only
    // overwrite when this entry actually carries the key — otherwise keep
    // whatever an already-processed richer entry set (FE-32783). Assumes
    // server-origin content objects, where an absent key (not requested) and
    // an explicit `undefined` value never occur together.
    const bodyExcerpt = Object.prototype.hasOwnProperty.call(
      content,
      'body_excerpt'
    )
      ? content.body_excerpt
      : existing.body_excerpt;

    // Gateways can drop pdf_export_enabled on sparse payloads (FE-35715) the
    // same way some homepage modules omit body_excerpt above — only overwrite
    // when this entry actually carries the key, otherwise keep whatever an
    // already-processed richer entry set. Same server-origin-object
    // assumption as body_excerpt: an absent key and an explicit `undefined`
    // never occur together.
    const pdfExportEnabled = Object.prototype.hasOwnProperty.call(
      content,
      'pdf_export_enabled'
    )
      ? content.pdf_export_enabled
      : existing.pdf_export_enabled;

    return {
      ...obj,
      [content.id]: {
        ...content,
        liked,
        bookmarked,
        reaction_counts: {
          ...content.reaction_counts,
          likes,
        },
        expanded_reactions: expandedReactions,
        body_excerpt: bodyExcerpt,
        pdf_export_enabled: pdfExportEnabled,
      },
    };
  }, {});

  return {
    ...state,
    ...newContents,
  };
};

const likeContent = (state, id) => {
  const likeCount = state[id].reaction_counts.likes || 0;

  return {
    ...state,
    [id]: {
      ...state[id],
      liked: true,
      reaction_counts: {
        ...state[id].reaction_counts,
        likes: likeCount + 1,
      },
    },
  };
};

const unlikeContent = (state, id) => {
  // Defaulting to 1 because we're immediately going to subtract a like, avoid "-1 Likes"
  const likeCount = state[id].reaction_counts.likes || 1;

  return {
    ...state,
    [id]: {
      ...state[id],
      liked: false,
      reaction_counts: {
        ...state[id].reaction_counts,
        likes: likeCount - 1,
      },
    },
  };
};

const updateContentCommentsCount = (state, id, val) => {
  const count = state[id].comment_count + val;
  return updateContent(state, id, { comment_count: count });
};

const updateContent = (state, id, attrs = {}) => ({
  ...state,
  [id]: {
    ...state[id],
    ...attrs,
  },
});

export default {
  acknowledgeContent,
  unacknowledgeContent,
  addContent,
  addContents,
  likeContent,
  unlikeContent,
  updateContentCommentsCount,
  updateContent,
};
